// Reflects the running timer in the browser tab so it stays visible from
// another tab: the title shows the elapsed time and the favicon becomes the
// Born Digital mark with its petals in a state colour. Idle restores the app's
// own title + icon (the mark in the brand gradient, public/favicon.svg).
//
// Colours are the design-system tokens in styles/tokens.css: cyan-400 for a
// running timer (cyan = live), warning-base for paused, error-base for stopped
// (waiting to be saved). The centre triangle stays steel-950 like the artwork.
const PETAL_COLORS = {
  running: '#51e4f7',
  paused: '#ffae21',
  stopped: '#e8455f',
};
const CENTRE_COLOR = '#0f1e25';

// The mark's geometry, 76x78 (Studio builder src/assets/icons/logoWhite.svg).
// Same paths as public/favicon.svg - keep the two in sync.
const PETALS = [
  'M33.282 42.4674L33.3584 30.2361L31.3728 33.7307L16.8628 59.1809L33.0529 59.0289L33.282 42.4674Z',
  'M52.9851 31.4514L62.913 37.2251L60.6984 33.5026L45.8065 8.35632L37.7878 22.4869L52.9851 31.4514Z',
  'M67.8005 45.5819L52.7559 54.0146L43.8972 58.9527H46.2646L75.59 58.7248L67.8005 45.5819Z',
  'M46.4174 64.1179H44.7373L68.5642 77.4128L68.4115 64.1179H46.4174Z',
  'M27.0963 30.7669L33.1294 20.3589L0.672852 39.5795L16.7866 48.544L27.0963 30.7669Z',
  'M65.7388 30.7671L68.0298 34.6416L67.5716 -0.000976562L52.9089 8.65967L65.7388 30.7671Z',
];
const CENTRE =
  'M60.7747 42.7707L50.0067 48.6964L39.3152 54.6222L39.4679 42.4669L39.6207 30.2356L50.2359 36.4652L60.7747 42.7707Z';

let baseTitle = null;
let baseIconTags = null;

// Capture the original title and favicon links on first use so we can put them
// back when the timer stops.
function ensureBase() {
  if (baseTitle === null) baseTitle = document.title;
  if (baseIconTags === null) {
    baseIconTags = Array.from(
      document.querySelectorAll('link[rel~="icon"]'),
    ).map((link) => link.outerHTML);
  }
}

// Chrome only reliably re-renders the tab icon when the <link> element is
// replaced, not when an existing href is mutated — and with several icons
// declared it may keep using another one (e.g. the .ico fallback). So drop
// every icon link and append a fresh one.
function replaceIconLinks(html) {
  document
    .querySelectorAll('link[rel~="icon"]')
    .forEach((link) => link.remove());
  document.head.insertAdjacentHTML('beforeend', html);
}

// A same-colour stroke with round joins gives the sharp polygons the rounded
// corners of the real artwork without closing the gaps between petals.
function shape(d, color) {
  return `<path d="${d}" fill="${color}" stroke="${color}"/>`;
}

// The mark as a 32px SVG with every petal in the status colour. Exported for
// tests; the app goes through setTabIcon.
export function markIcon(status) {
  const petals = PETAL_COLORS[status] || PETAL_COLORS.running;
  return (
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">' +
    '<g transform="translate(1.6 1.2) scale(0.38)" stroke-width="1.6" stroke-linejoin="round">' +
    PETALS.map((d) => shape(d, petals)).join('') +
    shape(CENTRE, CENTRE_COLOR) +
    '</g></svg>'
  );
}

function iconHref(status) {
  return `data:image/svg+xml,${encodeURIComponent(markIcon(status))}`;
}

export function setTabTitle(title) {
  ensureBase();
  document.title = title;
}

export function setTabIcon(status) {
  ensureBase();
  replaceIconLinks(
    `<link rel="icon" type="image/svg+xml" href="${iconHref(status)}">`,
  );
}

export function resetTab() {
  if (baseTitle !== null) document.title = baseTitle;
  if (baseIconTags !== null) replaceIconLinks(baseIconTags.join(''));
}
